import * as React from "react"
import { useNavigate } from "react-router-dom"

import { format } from "date-fns"

import User from "@podcastsoundboard/ps-lib/types/User"

import searchUsers from "../../../../api/users/search"
import Copyright from "../../../../components/Copyright"
import FilterContainer from "../../../../components/FilterContainer"
import HeadingContainer from "../../../../components/HeadingContainer"
import nameForUser from "../../../../helpers/strings/nameForUser"
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import AdminPanelSettingsIcon from "@podcastsoundboard/ps-lib/ui/icons/AdminPanelSettings"
import CheckIcon from "@podcastsoundboard/ps-lib/ui/icons/Check"
import EditIcon from "@podcastsoundboard/ps-lib/ui/icons/Edit"
import FilterListIcon from "@podcastsoundboard/ps-lib/ui/icons/FilterList"
import LocalAtmIcon from "@podcastsoundboard/ps-lib/ui/icons/LocalAtm"
import MarkEmailReadIcon from "@podcastsoundboard/ps-lib/ui/icons/MarkEmailRead"

let queryTimeout: NodeJS.Timeout

const PER_PAGE = 10

export default function AdminUserManagement() {
  const navigate = useNavigate()

  const [usersLoading, setUsersLoading] = React.useState(true)
  const [users, setUsers] = React.useState<User[] | null>(null)
  const [page, setPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(0)
  const [errorMessage, setErrorMessage] = React.useState("")
  const [isAdmin, setIsAdmin] = React.useState(false)
  const [subscribed, setSubscribed] = React.useState(false)

  const [email, setEmail] = React.useState("")
  const [name, setName] = React.useState("")

  const handleSearchUsers = React.useCallback(() => {
    searchUsers({
      email,
      name,
      page,
      perPage: PER_PAGE,
      subscribed,
      isAdmin,
    })
      .then(({ users, totalPages }) => {
        if (totalPages < page) setPage(totalPages)
        setUsers(users)
        setTotalPages(totalPages)
        setUsersLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setUsersLoading(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }, [email, isAdmin, name, page, subscribed])

  React.useEffect(() => {
    setUsersLoading(true)
    clearTimeout(queryTimeout)
    queryTimeout = setTimeout(() => {
      handleSearchUsers()
    }, 300)
  }, [name, email, page, handleSearchUsers])

  return (
    <div>
      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert className="rounded-none" severity="error">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <HeadingContainer>
        <Typography component="h1" variant="h6" noWrap color="textPrimary">
          Users
        </Typography>
      </HeadingContainer>

      <Divider />

      <FilterContainer>
        <FilterListIcon
          style={{ color: "var(--ps-text-primary)", fontSize: 22 }}
        />
        <Typography
          variant="body1"
          color="textPrimary"
          style={{
            fontSize: "13px",
            paddingLeft: "0.15rem",
            paddingRight: "1rem",
          }}
        >
          Filter
        </Typography>

        <TextField
          className="pr-4"
          size="small"
          value={email || ""}
          onChange={(e) => setEmail(e.target.value)}
          label="Email"
          variant="outlined"
        />

        <TextField
          className="pr-4"
          size="small"
          value={name || ""}
          onChange={(e) => setName(e.target.value)}
          label="Name"
          variant="outlined"
        />

        <FormControlLabel
          color="text.primary"
          control={
            <Checkbox
              checked={subscribed}
              onChange={(e) => setSubscribed(e.target.checked)}
            />
          }
          label="Subscribed"
        />

        <FormControlLabel
          color="text.primary"
          control={
            <Checkbox
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
            />
          }
          label="Admin"
        />
      </FilterContainer>

      <Divider className="mb-4" />

      {usersLoading && (
        <Box className="flex justify-center">
          <CircularProgress />
        </Box>
      )}

      {!usersLoading && (
        <Container maxWidth={false}>
          <Pagination
            className="mb-6"
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
          />

          <TableContainer component={Paper}>
            <Table className="min-w-[650px]" size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <b>Created At</b>
                  </TableCell>
                  <TableCell align="left">
                    <b>Email</b>
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {users &&
                  users.map((user) => (
                    <TableRow key={user.email}>
                      <TableCell align="left">
                        {format(new Date(user.createdAt), "Pp")}
                      </TableCell>
                      <TableCell align="left">
                        <Box>
                          {user.email}{" "}
                          {nameForUser(user) && `(${nameForUser(user)})`}
                        </Box>
                        {user.whoAmI && <Box>{user.whoAmI}</Box>}
                        {user.tellUsAboutYourself && (
                          <Box>{user.tellUsAboutYourself}</Box>
                        )}
                        {user.numberOneFeatureRequest && (
                          <Box>{user.numberOneFeatureRequest}</Box>
                        )}
                      </TableCell>
                      <TableCell align="right" className="pr-4">
                        {user.emailConfirmed && (
                          <Tooltip title="Email confirmed">
                            <IconButton>
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.subscribedToNewsletter && (
                          <Tooltip title="Subscribed to newsletter">
                            <IconButton>
                              <MarkEmailReadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.hasActiveStripeSubscription && (
                          <Tooltip
                            title={`Paid subscription (${
                              user.stripeSubscriptionCancelled
                                ? "ending"
                                : "renewing"
                            } on ${format(
                              new Date(user.stripeSubscriptionEndsAt),
                              "dd/MM/yyyy",
                            )}`}
                          >
                            <IconButton>
                              <LocalAtmIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.isAdmin && (
                          <Tooltip title="Admin">
                            <IconButton>
                              <AdminPanelSettingsIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() =>
                              navigate(`/app/admin/users/${user.uuid}`)
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      )}

      <Copyright className="pt-8" />
    </div>
  )
}
